import { Box, Button, IconButton, SxProps, Theme, Typography, useTheme } from '@mui/material';
import { memo, useMemo, useCallback } from 'react';
import IconLoader from '../../util-components/icon-loader/icon-loader';
import LightTooltip from '../tooltip/light-tooltip';
import TooltipPlacement from '../tooltip/tooltip-placement.enum';
import ButtonWrapperProps from './button-wrapper-props';
import IconSelector from '../../util-components/icon-loader/icon-selector';

const iconLoaderSx = { mr: 1 } as const;
const iconButtonSx = { mr: 0 } as const;
const boxSx = { display: 'inline-flex', m: 0, p: 0 } as const;

function ButtonWrapper({
	id,
	label,
	icon,
	onClick,
	size,
	ariaLabel,
	sx,
	variant,
	disabled,
	toolTip,
	iconPosition,
	ariaControls,
	ariaHaspopup,
	ariaExpanded,
	dataTour,
	highlightColor,
}: Readonly<ButtonWrapperProps>) {
	const theme = useTheme();

	const style = useMemo((): SxProps<Theme> => {
		let color = theme.palette.text.primary;
		let backgroundColor = 'transparent';
		let hoverColor = theme.palette.primary.dark;

		if (variant === 'light') {
			color = theme.palette.grey.A100;
			backgroundColor = theme.palette.primary.main;
			hoverColor = theme.palette.primary.light;
		}

		if (highlightColor) {
			backgroundColor = highlightColor;
		}

		return {
			color,
			borderRadius: 1,
			m: 0,
			py: 0.5,
			mr: 1,
			backgroundColor,
			':hover': {
				backgroundColor: hoverColor,
			},
			height: 'max-content',
			...sx,
		};
	}, [variant, highlightColor, sx, theme]);

	const tooltipTitle = useMemo(() => toolTip ?? ariaLabel ?? label, [toolTip, ariaLabel, label]);

	const iconLoaderProps = useMemo(
		() => ({
			sx: label === undefined ? iconButtonSx : iconLoaderSx,
			icon: icon ?? IconSelector.QuestionMark,
		}),
		[icon, label]
	);

	const renderIconButton = useCallback(
		() => (
			<IconButton
				id={id}
				aria-label={ariaLabel}
				size={size}
				onClick={onClick}
				sx={style}
				disabled={disabled}
				aria-controls={ariaControls}
				aria-haspopup={ariaHaspopup}
				aria-expanded={ariaExpanded}
			>
				{icon ? <IconLoader {...iconLoaderProps} /> : null}
			</IconButton>
		),
		[
			id,
			ariaLabel,
			size,
			onClick,
			style,
			disabled,
			ariaControls,
			ariaHaspopup,
			ariaExpanded,
			icon,
			iconLoaderProps,
		]
	);

	const renderLabelButton = useCallback(
		() => (
			<Button
				id={id}
				aria-label={ariaLabel}
				size={size}
				onClick={onClick}
				sx={style}
				disabled={disabled}
				startIcon={
					icon && (!iconPosition || iconPosition === 'left') ? <IconLoader icon={icon} /> : null
				}
				endIcon={icon && iconPosition === 'right' ? <IconLoader icon={icon} /> : null}
				aria-controls={ariaControls}
				aria-haspopup={ariaHaspopup}
				aria-expanded={ariaExpanded}
			>
				{typeof label === 'string' ? <Typography>{label}</Typography> : label}
			</Button>
		),
		[
			id,
			ariaLabel,
			size,
			onClick,
			style,
			disabled,
			icon,
			iconPosition,
			ariaControls,
			ariaHaspopup,
			ariaExpanded,
			label,
		]
	);

	if (!icon && !label) {
		console.error(`Button ${id} is not configured`);
		return <Typography>This button is not configured</Typography>;
	}

	return (
		<LightTooltip title={tooltipTitle} placement={TooltipPlacement.Bottom}>
			<Box sx={boxSx} data-tour={dataTour}>
				{!label ? renderIconButton() : renderLabelButton()}
			</Box>
		</LightTooltip>
	);
}

export default memo(ButtonWrapper);
